import { config } from './config';

/**
 * Countly SDK wrapper. Mirrors v1's `clinician/src/countly/countly.ts`
 * one-for-one — same function shapes so call sites can be migrated by
 * direct copy. Notable differences from v1:
 *
 *   • The SDK is lazy-imported (dynamic `import('countly-sdk-web')`) so
 *     test bundles + the boot critical path don't pay its cost.
 *   • Every wrapper is safe to call before init resolves — calls made
 *     while the SDK is still loading are queued and replayed once it's
 *     ready (without this, the first few seconds of the session would
 *     silently drop events). Once init either resolves OR fails, the
 *     queue is drained and stops accepting entries.
 *   • All errors are swallowed. Analytics must never break the app.
 *
 * Init is idempotent; safe to call from `main.tsx` under Strict Mode
 * double-invocation or Vite HMR re-runs.
 */

type CountlySdk = {
  init: (opts: Record<string, unknown>) => void;
  track_sessions: () => void;
  track_errors: () => void;
  track_pageview: (
    name?: string,
    ignoreList?: string[],
    segmentation?: Record<string, string>,
  ) => void;
  add_event: (event: {
    key: string;
    count?: number;
    sum?: number;
    segmentation?: Record<string, string | number | boolean>;
  }) => void;
  change_id: (newId: string, merge: boolean) => void;
  get_device_id: () => string | undefined;
  log_error: (err: Error, segments?: Record<string, string>) => void;
  userData: {
    set: (key: string, value: string) => void;
    save: () => void;
  };
};

let sdk: CountlySdk | null = null;
/** True once init has been called once; covers both the
 *  loading-in-flight state and the resolved/failed terminal states. */
let initStarted = false;
/** Queued operations that arrived before the SDK finished loading.
 *  Bounded so a pathological boot doesn't grow unbounded. Drained
 *  exactly once when the SDK resolves (or cleared on failure). */
type QueuedOp = (sdk: CountlySdk) => void;
const queue: QueuedOp[] = [];
const MAX_QUEUED = 200;

const enqueue = (op: QueuedOp) => {
  if (sdk) {
    safeRun(op);
    return;
  }
  if (queue.length >= MAX_QUEUED) return; // drop silently
  queue.push(op);
};

const safeRun = (op: QueuedOp) => {
  if (!sdk) return;
  try {
    op(sdk);
  } catch {
    // never propagate
  }
};

export function initCountly() {
  if (initStarted) return;
  initStarted = true;
  if (!config.COUNTLY_APP_KEY || !config.COUNTLY_URL) {
    // Bail without queue replay — there's no SDK to drain into. Existing
    // queue entries are abandoned by design.
    return;
  }
  import('countly-sdk-web')
    .then((mod) => {
      const Countly = ((mod as unknown as { default?: CountlySdk }).default ?? mod) as CountlySdk;
      Countly.init({
        app_key: config.COUNTLY_APP_KEY,
        url: config.COUNTLY_URL,
        debug: config.IS_LOCAL,
        app_version: config.RELEASE,
        session_update: 60,
        use_session_cookie: true,
      });
      Countly.track_sessions();
      Countly.track_errors();
      sdk = Countly;
      // Drain pending operations in arrival order.
      while (queue.length > 0) {
        const op = queue.shift()!;
        safeRun(op);
      }
    })
    .catch(() => {
      // SDK failed to load — drop queued ops so they don't linger.
      queue.length = 0;
    });
}

/** True after the SDK has loaded successfully. */
export function isCountlyReady(): boolean {
  return sdk !== null;
}

/**
 * Track an SPA page view. Mirrors v1; safe to call on every route change.
 *
 * @param viewName        Route path or human-readable page name.
 * @param viewSegmentation Optional key-value pairs attached to the view event.
 */
export function trackView(
  viewName: string,
  viewSegmentation?: Record<string, string>,
) {
  enqueue((s) => s.track_pageview(viewName, undefined, viewSegmentation));
}

/**
 * Record a custom event.
 *
 * @param key          Event name (e.g. "Meal Added").
 * @param segmentation Arbitrary key-value metadata. Sanitize PII at the
 *                     `track()` wrapper layer, not here.
 * @param count        Number of occurrences (default 1).
 * @param sum          Numeric value to aggregate (e.g. dollar amount).
 */
export function trackEvent(
  key: string,
  segmentation?: Record<string, string | number | boolean>,
  count: number = 1,
  sum?: number,
) {
  enqueue((s) => s.add_event({ key, count, sum, segmentation }));
}

/**
 * Identify the current user and merge their anonymous session into the
 * profile. Call once after login / auth hydration.
 */
export function identifyUser(
  userId: string,
  merge: boolean = true,
  userProperties?: Record<string, string>,
) {
  enqueue((s) => {
    s.change_id(userId, merge);
    if (userProperties) {
      for (const [k, v] of Object.entries(userProperties)) {
        s.userData.set(k, v);
      }
      s.userData.save();
    }
  });
}

/**
 * Reset the device ID to a new SDK-generated anonymous value. Call on
 * logout so the next visitor gets a clean anonymous session.
 */
export function resetUser() {
  enqueue((s) => {
    const anonId = `anon-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    s.change_id(anonId, false);
  });
}

/**
 * True when Countly has a persisted device id that doesn't look like an
 * anonymous SDK-generated one — used by `useAnalyticsUserIdentity` to
 * spot a stale post-logout session and reset it.
 */
export function hasStaleUserSession(): boolean {
  if (!sdk) return false;
  try {
    const deviceId = sdk.get_device_id();
    return typeof deviceId === 'string' && !deviceId.startsWith('anon-');
  } catch {
    return false;
  }
}

/**
 * Drop event-segmentation entries that would otherwise leak PII. We
 * strip patient ids (most common leak vector in v1 events) and any
 * non-primitive values (`{}`, `[]`, server response blobs that
 * accidentally got passed through). Mirrors v1's sanitizeSegmentation.
 */
const SENSITIVE_KEY_PATTERN = /patient.?id|patientid/i;

export function sanitizeSegmentation(
  properties: Record<string, unknown>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(properties)) {
    if (v == null) continue;
    if (SENSITIVE_KEY_PATTERN.test(k)) continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Replace numeric path segments with `:id` so analytics view names don't
 * leak patient/user ids. e.g. `/patients/12345/summary` → `/patients/:id/summary`.
 */
export function parameterizePath(pathname: string): string {
  return pathname.replace(/\/\d+/g, '/:id');
}

/** Manually report an error to Countly's crash analytics. Auto JS error
 *  tracking is already on via `track_errors()` in init; this is for
 *  explicit logging from catch blocks. */
export function logError(
  error: Error | string,
  segments?: Record<string, string>,
) {
  enqueue((s) => {
    const err = typeof error === 'string' ? new Error(error) : error;
    s.log_error(err, segments);
  });
}
